import React, { useState, useEffect } from 'react';
import { Download, FileSpreadsheet, Package, Truck, AlertTriangle, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useMyContext } from '../../../context/MyContext';
import NavBar from '../../../component/b2c/navBar/navBar';
import Sidebar from '../../../component/b2c/sidebar/sidebar';
import { Base_Url, forwardorderlistall } from '../../../config/config';
import { TbReportSearch } from "react-icons/tb";
import '../../../css/custom-scrollbar.css'
import { toast } from 'react-toastify';
import Papa from "papaparse";
import FullPageLoader from '../../../../src/component/fullPageLoader/fullPageLoader'

// Icon + gradient accent per report, purely visual — keeps each card visually distinct
const REPORT_STYLES = {
  'MIS Report': { icon: FileSpreadsheet, gradient: 'from-violet-500 to-purple-600', ring: 'ring-violet-500/20' },
  'Order Product Report': { icon: Package, gradient: 'from-indigo-500 to-blue-600', ring: 'ring-indigo-500/20' },
  'Current Shipment Report': { icon: Truck, gradient: 'from-purple-500 to-fuchsia-600', ring: 'ring-purple-500/20' },
  'NDR Report': { icon: AlertTriangle, gradient: 'from-fuchsia-500 to-pink-600', ring: 'ring-fuchsia-500/20' },
};

const ReportsDropdown = () => {
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const navigate = useNavigate();
  const [isPageLoading, isSetPageLoading] = useState();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRanges, setDateRanges] = useState({
    'MIS Report': { startDate: '', endDate: '' },
    'Order Product Report': { startDate: '', endDate: '' },
    'Current Shipment Report': { startDate: '', endDate: '' },
    'NDR Report': { startDate: '', endDate: '' }
  });

  const validateDate = (date) => {
    const selectedDate = new Date(date);
    const today = new Date();
    return selectedDate <= today;
  };

  // Fixed column mapping - mapping display names to actual API keys
  const reports = [
    {
      name: 'MIS Report',
      description: 'Management information system report with detailed order',
      columnMapping: {
        'Order ID': 'order_id',
        'User ID': 'user_id',
        'AWB Number': 'awb_number',
        'Pickup Name': 'pickup_name',
        'Pickup Address': 'pickup_address',
        'Pickup City': 'pickup_city',
        'Pickup Pin Code': 'pickup_pincode',
        'Pickup Country': 'pickup_country',
        'Pickup Phone': 'pickup_phone',
        'First Name': 'first_name',
        'Last Name': 'last_name',
        'Drop Address': 'drop_address',
        'Drop Pin': 'drop_pin',
        'Drop City': 'drop_city',
        'Drop State': 'drop_state',
        'Phone': 'phone',
        'Email': 'email',
        'Payment Mode': 'payment_mode',
        'Total Amount': 'total_amount',
        'Created At': 'created_at',
        'Partner': 'partner',
        'Warehouse ID': 'warehouse_id',
        'Shipment Type': 'shipment_type',
        'Payment ID': 'payment_id',
        'Product Description': 'product_desc',
        'COD Amount': 'cod_amount',
        'Quantity': 'quantity',
        'Width': 'width',
        'Height': 'height',
        'Length': 'length',
        'Weight': 'weight',
        'Shipping Mode': 'shipping_mode',
        'Address Type': 'address_type',
        'Request ID': 'request_id',
        'Is NDR': 'is_ndr',
        'Status': 'status',
        'Debit Amount': 'debit_amount'
      }
    },
    {
      name: 'Order Product Report',
      description: 'Detailed product information for all orders',
      columnMapping: {
        'Order ID': 'order_id',
        'User ID': 'user_id',
        'First Name': 'first_name',
        'Last Name': 'last_name',
        'Product Description': 'product_desc',
        'Quantity': 'quantity',
        'SKU': 'sku', // Note: This field might not exist in your API response
        'AWB Number': 'awb_number',
        'Created At': 'created_at'
      }
    },
    {
      name: 'Current Shipment Report',
      description: 'Real-time status of all active shipments',
      columnMapping: {
        'Order ID': 'order_id',
        'User ID': 'user_id',
        'First Name': 'first_name',
        'Last Name': 'last_name',
        'Product Description': 'product_desc',
        'Quantity': 'quantity',
        'SKU': 'sku', // Note: This field might not exist in your API response
        'AWB Number': 'awb_number',
        'Drop Address': 'drop_address',
        'Drop City': 'drop_city',
        'Drop State': 'drop_state',
        'Drop Pin': 'drop_pin',
        'Status': 'status'
      }
    },
    {
      name: 'NDR Report',
      description: 'Non-delivery report with attempt details and reasons',
      columnMapping: {
        'Order ID': 'order_id',
        'First Name': 'first_name',
        'User ID': 'user_id',
        'Last Name': 'last_name',
        'Phone': 'phone',
        'Product Description': 'product_desc',
        'AWB Number': 'awb_number',
        'Drop Address': 'drop_address',
        'Drop Pin': 'drop_pin',
        'Drop City': 'drop_city',
        'Drop State': 'drop_state',
        'Status': 'status',
        'NDR Attempt': 'ndr_attempt', // Note: This field might not exist in your API response
        'Updated At': 'updated_at',
        'NDR Reason': 'ndr_reason' // Note: This field might not exist in your API response
      }
    }
  ];

  useEffect(() => {
    fetchOrderData();
  }, []);
  const fetchOrderData = async () => {
    setLoading(true);
    setError(null);
    isSetPageLoading(true);
    const token = localStorage.getItem('token');
    const cusid = localStorage.getItem('cus_id');
    try {
      const response = await fetch(Base_Url + forwardorderlistall, {
        method: 'GET',
        headers: {
          token,
          cus_id: cusid,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();

      if (response.ok) {
        setData(result.data || []);
        isSetPageLoading(false);
      } else {
        setError(result.message);
        isSetPageLoading(false);
      }
    } catch (error) {
      setError('Failed to fetch data. Please try again later.');
      isSetPageLoading(false);

    } finally {
      setLoading(false);
      isSetPageLoading(false);
    }
  };

  const handleDateChange = (reportName, field, value) => {
    if (value && !validateDate(value)) {
      toast.error('Future dates are not allowed');
      return;
    }

    // For end date, check if it's not before start date
    if (field === 'endDate' && value) {
      const startDate = dateRanges[reportName].startDate;
      if (startDate && new Date(value) < new Date(startDate)) {
        toast.error('End date cannot be before start date');
        return;
      }
    }

    // For start date, check if it's not after end date
    if (field === 'startDate' && value) {
      const endDate = dateRanges[reportName].endDate;
      if (endDate && new Date(value) > new Date(endDate)) {
        toast.error('Start date cannot be after end date');
        return;
      }
    }

    setDateRanges(prev => ({
      ...prev,
      [reportName]: {
        ...prev[reportName],
        [field]: value
      }
    }));
  };

  const isDownloadDisabled = (reportName) => {
    const { startDate, endDate } = dateRanges[reportName];
    return loading || !startDate || !endDate;
  };

  const downloadCSV = async (report) => {
    const { startDate, endDate } = dateRanges[report.name];

    // Filter data by date range if provided
    let filteredData = data;
    if (startDate && endDate) {
      filteredData = data.filter(item => {
        const itemDate = new Date(item.created_at);
        const start = new Date(startDate);
        const end = new Date(endDate);
        // Set end date to end of day for proper comparison
        end.setHours(23, 59, 59, 999);
        return itemDate >= start && itemDate <= end;
      });
    }

    // Check if we have data to export
    if (filteredData.length === 0) {
      toast.error('No data found for the selected date range');
      return;
    }

    // Transform data using the column mapping
    const csvData = filteredData.map(item => {
      const row = {};
      Object.entries(report.columnMapping).forEach(([displayName, apiKey]) => {
        // Get the value from the API response using the correct key
        row[displayName] = item[apiKey] !== undefined && item[apiKey] !== null ? item[apiKey] : 'N/A';
      });
      return row;
    });

    try {
      // Use Papa Parse to convert to CSV
      const csv = Papa.unparse(csvData, {
        header: true,
        quotes: true,
        quoteChar: '"',
        escapeChar: '"'
      });

      // Create and download the file
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.name}_${startDate || 'all'}_to_${endDate || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`${report.name} downloaded successfully!`);
    } catch (error) {
      console.error('Error generating CSV:', error);
      toast.error('Failed to generate CSV file');
    }
  };

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'bg-[#0f0b1f]' : 'bg-gradient-to-br from-slate-50 via-purple-50/40 to-indigo-50/40'}`}>
      <div className="z-10">
        <NavBar />
      </div>
      <div className="flex flex-1 overflow-hidden relative">
        {/* Ambient background glow, purely decorative */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-purple-400/20 blur-3xl dark:bg-purple-700/20" />
          <div className="absolute top-1/3 -right-24 h-72 w-72 rounded-full bg-indigo-400/20 blur-3xl dark:bg-indigo-700/20" />
        </div>

        {isToggleSidebar && (
          <div className={`fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20`}
            style={
              window.innerWidth < 768
                ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' }
                : {}
            }>
            <Sidebar />
          </div>
        )}
        <FullPageLoader
          isLoading={isPageLoading}
          message="Processing your request..."
        />
        <div className="relative z-10  bg-gray-200  w-full overflow-y-auto custom-scrollbar">
          <div className="flex-1 overflow-auto  p-2 sm:p-2">

            {/* Header */}
            <div className="mb-2 flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-600 to-indigo-600 shadow-lg shadow-purple-500/30">
                <TbReportSearch className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  Reports Dashboard
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Pick a date range and export the report you need
                </p>
              </div>
            </div>

            {error && (
              <div className="mb-6 rounded-2xl border border-red-200/60 bg-red-50/80 backdrop-blur-sm p-4 dark:border-red-900/40 dark:bg-red-950/30">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            <div className="grid gap-5 md:grid-cols-2">
              {reports.map((report, idx) => {
                const style = REPORT_STYLES[report.name];
                const Icon = style.icon;
                const disabled = isDownloadDisabled(report.name);

                return (
                  <motion.div
                    key={report.name}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: idx * 0.06 }}
                    className={`group relative rounded-2xl border border-white/60 bg-white/70 backdrop-blur-xl shadow-sm ring-1 ${style.ring} transition-all hover:shadow-xl hover:shadow-purple-500/10 hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/5 p-2`}
                  >
                    <div className="mb-2 flex items-start gap-3">
                      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${style.gradient} shadow-md`}>
                        <Icon className="text-white" size={20} />
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                          {report.name}
                        </h2>
                        <p className=" text-sm text-gray-500 dark:text-gray-400">
                          {report.description}
                        </p>
                        <p className=" text-xs font-medium text-purple-500/80 dark:text-purple-300/70">
                          {Object.keys(report.columnMapping).length} fields included
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <label className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                            <Calendar size={12} /> Start Date
                          </label>
                          <input
                            type="date"
                            value={dateRanges[report.name].startDate}
                            onChange={(e) => handleDateChange(report.name, 'startDate', e.target.value)}
                            className="w-full rounded-xl border border-gray-200 bg-white/80 px-3 py-2 text-sm text-gray-700 shadow-sm outline-none transition focus:border-purple-400 focus:ring-2 focus:ring-purple-400/30 dark:border-white/10 dark:bg-white/5 dark:text-gray-200"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                            <Calendar size={12} /> End Date
                          </label>
                          <input
                            type="date"
                            value={dateRanges[report.name].endDate}
                            onChange={(e) => handleDateChange(report.name, 'endDate', e.target.value)}
                            className="w-full rounded-xl border border-gray-200 bg-white/80 px-3 py-2 text-sm text-gray-700 shadow-sm outline-none transition focus:border-purple-400 focus:ring-2 focus:ring-purple-400/30 dark:border-white/10 dark:bg-white/5 dark:text-gray-200"
                          />
                        </div>
                      </div>

                      <motion.button
                        whileTap={{ scale: disabled ? 1 : 0.98 }}
                        onClick={() => downloadCSV(report)}
                        disabled={disabled}
                        className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-white shadow-md transition-all ${
                          disabled
                            ? 'cursor-not-allowed bg-gray-300 shadow-none dark:bg-white/10'
                            : `bg-gradient-to-r ${style.gradient} hover:shadow-lg hover:shadow-purple-500/20`
                        }`}
                      >
                        <Download size={16} />
                        Download CSV
                      </motion.button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsDropdown;